import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface FixedLevel {
  level: number;
  toArabic: boolean;
  instruction: string;
  values: Array<string | number>;
}

interface RandomLevel {
  level: number;
  toArabic: boolean;
  instruction: string;
  random: { max: number; count: number };
}

type Level = FixedLevel | RandomLevel;

const LEVELS = levelsData.levels as Level[];

const ROMAN_KEYS = ['I', 'V', 'X', 'L', 'C', 'D', 'M'] as const;
const ARABIC_RE = /^[0-9]*$/;
const ROMAN_RE = /^[IVXLCDM]*$/;

function arabicToRoman(num: number): string {
  if (!num || num < 1 || num > 3999) return '';
  const digits = String(num).split('');
  const key = [
    '', 'C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM',
    '', 'X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC',
    '', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX',
  ];
  let roman = '';
  let i = 3;
  while (i--) {
    const d = digits.pop() ?? '0';
    roman = (key[Number(d) + i * 10] ?? '') + roman;
  }
  const thousands = Number(digits.join('') || '0');
  return 'M'.repeat(thousands) + roman;
}

function romanToArabic(str: string): number {
  const upper = str.toUpperCase();
  if (!upper) return 0;
  const validator = /^M*(?:D?C{0,3}|C[MD])(?:L?X{0,3}|X[CL])(?:V?I{0,3}|I[XV])$/;
  if (!validator.test(upper)) return 0;
  const map: Record<string, number> = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 };
  const token = /[MDLV]|C[MD]?|X[CL]?|I[XV]?/g;
  let num = 0;
  let m: RegExpExecArray | null;
  while ((m = token.exec(upper))) num += map[m[0]] ?? 0;
  return num;
}

function isFixed(l: Level): l is FixedLevel {
  return 'values' in l;
}

function buildQuestions(level: Level): Array<{ value: string | number }> {
  if (isFixed(level)) return level.values.map((v) => ({ value: v }));
  const out: Array<{ value: string | number }> = [];
  for (let i = 0; i < level.random.count; i++) {
    const n = Math.floor(Math.random() * level.random.max) + 1;
    out.push({ value: n });
  }
  return out;
}

export function mountRomanNumerals(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;

  let questions = buildQuestions(level);
  let subIdx = 0;
  let answer = '';
  let correctCount = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Números romanos</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage roman">
      <div class="feedback" aria-live="polite"></div>
      <p class="roman__progress" aria-live="polite"></p>
      <p class="roman__question" aria-live="polite"></p>
      <div class="roman__input-row">
        <div class="roman__input" aria-label="Sua resposta"></div>
        <div class="roman__feedback-value" aria-live="polite"></div>
      </div>
      <div class="roman__keypad" role="group" aria-label="Teclado"></div>
      <button class="btn btn--primary roman__ok" type="button">Confirmar</button>
      ${level.instruction ? `<p class="roman__instruction">${level.instruction.replace(/\n/g, '<br>')}</p>` : ''}
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.roman__progress')!;
  const questionEl = page.querySelector<HTMLElement>('.roman__question')!;
  const inputEl = page.querySelector<HTMLElement>('.roman__input')!;
  const feedbackValueEl = page.querySelector<HTMLElement>('.roman__feedback-value')!;
  const keypadEl = page.querySelector<HTMLElement>('.roman__keypad')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.roman__ok')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function renderKeypad(): void {
    keypadEl.innerHTML = '';
    const labels: string[] = level.toArabic
      ? ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
      : [...ROMAN_KEYS];
    for (const label of labels) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'roman__key';
      btn.textContent = label;
      btn.addEventListener('click', () => append(label));
      keypadEl.appendChild(btn);
    }
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'roman__key roman__key--back';
    back.textContent = '⌫';
    back.addEventListener('click', backspace);
    keypadEl.appendChild(back);
  }

  function renderQuestion(): void {
    const q = questions[subIdx]!;
    progressEl.textContent = `${subIdx + 1} de ${questions.length}`;
    if (level.toArabic) {
      questionEl.textContent = `Converta o número romano ${q.value} para arábico.`;
    } else {
      questionEl.textContent = `Converta o número ${q.value} para romano.`;
    }
    answer = '';
    renderInput();
  }

  function renderInput(): void {
    inputEl.textContent = answer || ' ';
    if (level.toArabic) {
      const n = answer ? Number(answer) : 0;
      feedbackValueEl.textContent = n ? `Valor: ${n}` : '';
    } else {
      const n = romanToArabic(answer);
      feedbackValueEl.textContent = n ? `Valor: ${n}` : '';
    }
  }

  function append(ch: string): void {
    const re = level.toArabic ? ARABIC_RE : ROMAN_RE;
    const next = answer + ch.toUpperCase();
    if (!re.test(next)) return;
    answer = next;
    ctx.audio.play('click');
    renderInput();
  }

  function backspace(): void {
    if (!answer) return;
    answer = answer.slice(0, -1);
    ctx.audio.play('click');
    renderInput();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 600);
  }

  function check(): void {
    if (!answer) {
      showFeedback('err');
      ctx.audio.play('wrong');
      return;
    }
    const expected = questions[subIdx]!.value;
    const ok = level.toArabic
      ? Number(answer) === romanToArabic(String(expected))
      : answer.toUpperCase() === arabicToRoman(Number(expected));
    if (ok) {
      correctCount++;
      ctx.audio.play('correct');
      showFeedback('ok');
      subIdx++;
      window.setTimeout(() => {
        if (subIdx >= questions.length) finishLevel();
        else renderQuestion();
      }, 600);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      inputEl.classList.add('roman__input--wrong');
      window.setTimeout(() => inputEl.classList.remove('roman__input--wrong'), 500);
    }
  }

  okBtn.addEventListener('click', check);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') { check(); e.preventDefault(); return; }
    if (e.key === 'Backspace') { backspace(); e.preventDefault(); return; }
    const k = e.key.toUpperCase();
    if (level.toArabic) {
      if (k >= '0' && k <= '9') { append(k); e.preventDefault(); }
    } else {
      if ((ROMAN_KEYS as readonly string[]).includes(k)) { append(k); e.preventDefault(); }
    }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${questions.length}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/roman_numerals/${level.level + 1}`; });
  }

  renderKeypad();
  renderQuestion();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}
